/**
 * Sub-aba "Roteiro Cliente": a sequência sugerida dos 8 produtos no atendimento, reordenada
 * arrastando, com um ÚNICO "Salvar alterações" no rodapé. Porte de `admin.html:2096-2099` e
 * `9459-9519`; linhas 143 e 144 do inventário.
 *
 * SOLTAR NÃO GRAVA MAIS, por pedido do fundador em 07/10/2026 (o mesmo pedido que trocou o "Salvar"
 * por linha das outras sub-abas de Configuração por um botão único). O legado gravava no `dragend` e
 * no `pointerup` (`admin.html:9504`), então cada arraste era uma ida ao banco, e um toque na linha
 * sem mexer nada também gravava. Agora a ordem arrastada fica NA TELA até o clique, e o botão só
 * liga quando ela difere da que veio do banco.
 *
 * A ORDEM ARRASTADA SOBREVIVE porque ela mora no estado do `useArrasteDeLinhas`, e o efeito que a
 * devolve para a do banco só dispara quando as chaves mudam. Como `original` só muda ao ler e ao
 * gravar, o que foi arrastado continua onde a pessoa deixou.
 *
 * A NUMERAÇÃO ANDA COM O PRODUTO, e não com a posição, durante o arraste: ela sai de `original`, e
 * não da ordem ao vivo. É o mesmo efeito do legado, que movia o nó do DOM com o texto "3. Coloração
 * Pessoal" dentro e só renumerava ao redesenhar a lista depois de gravar. O que mudou é quando isso
 * acontece: antes era ao soltar, agora é depois do clique em "Salvar alterações".
 *
 * OS STATUS DESTA SUB-ABA NÃO SOMEM SOZINHOS: o legado escreve direto no parágrafo, sem relógio
 * (`admin.html:9507`, `9512` e `9518`), e o texto fica até a lista ser relida.
 */

// Estado, memória e efeito do React.
import { useCallback, useEffect, useState } from 'react';
// O botão do Chassi: o único Salvar desta tela, e o que cria a sequência inicial.
import { Botao } from '@complexity/ui';
// As chamadas da área.
import { listarRoteiroProdutos, salvarOrdemDoRoteiro, semearRoteiroProdutos } from '@studiolu/dados/areas/sistema';
// A linha de `roteiro_produtos`.
import type { LinhaRoteiroProdutos } from '@studiolu/dados';
// Sessão e fonte de dados.
import { usePainel } from '../../contexto.js';
// O parágrafo de status; aqui só a versão que não some (`fixar`).
import { LinhaDeStatus, useStatusTemporario } from '../../comum/status-temporario.js';
// Os nomes dos produtos pela chave do lead, que é a chave desta tabela (`PRODUTOS_LEAD`).
import { PRODUTOS_LEAD } from '../ficha-cliente/comum-ficha/catalogo.js';
// O arraste de mouse e de toque.
import { useArrasteDeLinhas } from './arraste.js';
// As peças visuais da área.
import { AlcaDeArraste, CartaoParam, LinhaParam, NotaSecao, TextoParam, type Carga, type PropsDaSubAba } from './pecas.js';

/** As linhas na ordem salva; sem ordem vão para o fim, como o `?? 999` do legado (`admin.html:9485`). */
function emOrdem(linhas: readonly LinhaRoteiroProdutos[]): LinhaRoteiroProdutos[] {
  return [...linhas].sort((a, b) => (a.ordem ?? 999) - (b.ordem ?? 999));
}

/**
 * A SEQUÊNCIA INICIAL, em ordem alfabética do nome em português, que é o que a nota da sub-aba
 * promete ("Começa em ordem alfabética até a primeira vez que for reordenado").
 *
 * AS CHAVES SÃO AS DO LEAD, e isto foi medido, não lembrado: `roteiro_produtos.produto` carrega a
 * chave de `PRODUTOS_LEAD` (`eip`, `mala_viagem`, `montagem_looks`, `analise_proporcao_3d`), e não
 * a chave de parâmetro de `PARAM_PRODUTOS_LABEL` (`estrategia_imagem_pessoal_profissional` e
 * companhia). Quem prova são dois pontos do legado: o "Próximo passo sugerido" cruza `r.produto`
 * com os produtos que o lead tem e rotula por `PRODUTOS_LEAD` (`admin.html:9543`), e esta tela
 * rotula pelo mesmo mapa. Semear com a outra família de chaves mostraria a chave crua na lista e
 * quebraria aquela sugestão em silêncio.
 */
const SEQUENCIA_INICIAL: readonly string[] = Object.keys(PRODUTOS_LEAD).sort((a, b) =>
  (PRODUTOS_LEAD[a] ?? a).localeCompare(PRODUTOS_LEAD[b] ?? b, 'pt-BR'),
);

export function SubAbaRoteiro({ recarga }: Pick<PropsDaSubAba, 'recarga'>) {
  const { dados } = usePainel();
  const [carga, setCarga] = useState<Carga<null>>({ tipo: 'carregando' });
  /**
   * O QUE O BANCO TEM: os produtos na ordem lida, guardado à parte do que está na tela.
   *
   * É a diferença entre ele e a ordem ao vivo do arraste que diz se há o que gravar. Sem guardar o
   * original, o botão único não teria como saber se a pessoa mexeu em algo, e um clique gravaria as
   * oito linhas de novo com a mesma ordem que já estava lá.
   */
  const [original, setOriginal] = useState<readonly string[]>([]);
  // Trava o botão durante a gravação: clicar duas vezes gravaria a mesma coisa duas vezes.
  const [salvando, setSalvando] = useState(false);
  // O mesmo para a semeadura: dois cliques criariam a sequência duas vezes.
  const [semeando, setSemeando] = useState(false);
  // `#param-roteiro-produtos-status`: só o `fixar`, porque aqui nada some sozinho.
  const [status, , fixarStatus] = useStatusTemporario();

  // `carregarRoteiroProdutos` (`admin.html:9467-9477`).
  const carregar = useCallback(async () => {
    setCarga({ tipo: 'carregando' });
    // O status mora dentro da lista no legado, e some quando a lista é reescrita.
    fixarStatus('');
    const resultado = await listarRoteiroProdutos(dados);
    if (!resultado.ok) {
      setCarga({ tipo: 'erro', mensagem: resultado.erro.mensagem });
      return;
    }
    // O retrato do banco: os produtos na ordem salva. Daqui em diante, diferença é alteração.
    setOriginal(emOrdem(resultado.dados ?? []).map((r) => r.produto));
    setCarga({ tipo: 'pronta', dados: null });
  }, [dados, fixarStatus]);

  // Lê ao abrir a área e a cada clique na sub-aba.
  useEffect(() => {
    void carregar();
  }, [carregar, recarga]);

  // O número de cada produto, pela ordem QUE ESTÁ NO BANCO (ver o cabeçalho).
  const numero = new Map(original.map((produto, indice) => [produto, indice + 1]));

  /**
   * O arraste só mexe na ordem ao vivo: o `aoTerminar` não grava mais nada.
   *
   * ELE CONTINUA EXISTINDO porque o gancho o chama no `dragend` e no `pointerup`, e tirá-lo pediria
   * mexer no gancho, que é compartilhado com o "Vínculo Produto Aba" e não faz parte deste pedido.
   * Com ele vazio, soltar a linha deixa a ordem na tela, que é exatamente o que o fundador pediu.
   */
  const arraste = useArrasteDeLinhas(original, () => {});

  // A ordem mudou? É a comparação que liga o botão, e o `join` basta porque o conjunto é o mesmo.
  const ordemAlterada = arraste.ordem.join('|') !== original.join('|');

  /**
   * "Salvar alterações": UM botão para a tela inteira, por pedido do fundador em 07/10/2026, igual
   * ao das sub-abas "Visibilidade Aba" e "Preço Padrão Produto".
   *
   * É UMA CHAMADA SÓ, e não um laço de oito como nas outras duas sub-abas, porque aqui o que mudou
   * é a SEQUÊNCIA, e não uma linha de cada vez: mover um produto muda o índice de todos os que
   * estão entre a posição antiga e a nova. Meia sequência gravada seria uma ordem que não existe em
   * lugar nenhum, então a gravação é uma e, falhando, nada avança.
   *
   * FALHOU? `original` fica como estava, o botão continua ligado e a pessoa pode tentar de novo sem
   * precisar arrastar outra vez. No legado a ordem ficava na tela com os números antigos e sem
   * caminho de volta, e esse pedaço fica igual: a lista não desfaz o arraste.
   */
  const salvarOrdem = useCallback(async () => {
    if (!ordemAlterada || salvando) return;
    // A ordem é fotografada ANTES da ida ao banco: um arraste durante a gravação não pode mudar o
    // que está sendo gravado, senão o `original` passaria a valer uma ordem que ninguém gravou.
    const nova = [...arraste.ordem];
    setSalvando(true);
    fixarStatus('Salvando ordem...');
    const resultado = await salvarOrdemDoRoteiro(dados, nova, new Date().toISOString());
    if (!resultado.ok) {
      setSalvando(false);
      fixarStatus('Erro ao salvar ordem: ' + resultado.erro.mensagem);
      return;
    }
    // O original avança para a ordem gravada: o botão desliga e a lista renumera.
    setOriginal(nova);
    setSalvando(false);
    fixarStatus('Ordem salva.');
  }, [ordemAlterada, salvando, arraste.ordem, dados, fixarStatus]);

  /**
   * "Criar sequência inicial", por decisão do fundador em 07/10/2026, 17:36.
   *
   * O PROBLEMA QUE ELE RESOLVE, medido em produção: `roteiro_produtos` está vazia, porque nenhuma
   * migration semeia a tabela e o legado nunca inseriu linha. Sem isto, a única saída seria um
   * comando de banco, que depende de credencial que ninguém tem à mão no dia a dia.
   *
   * Depois de criar, a lista é RELIDA do banco em vez de montada aqui: assim o que aparece na tela
   * é o que ficou gravado, e não o que esta função pediu para gravar.
   */
  const semear = useCallback(async () => {
    if (semeando) return;
    setSemeando(true);
    fixarStatus('Criando a sequência...');
    const resultado = await semearRoteiroProdutos(dados, SEQUENCIA_INICIAL, new Date().toISOString());
    if (!resultado.ok) {
      // O botão volta a ficar clicável, e o motivo fica na tela, como todo status desta sub-aba.
      setSemeando(false);
      fixarStatus('Erro ao criar a sequência: ' + resultado.erro.mensagem);
      return;
    }
    await carregar();
    setSemeando(false);
    fixarStatus('Sequência criada.');
  }, [semeando, dados, carregar, fixarStatus]);

  return (
    <div className="flex flex-col gap-cx-4">
      <NotaSecao>
        Define a sequência sugerida dos produtos no roteiro de atendimento da cliente. Arraste pra reordenar. Começa em ordem
        alfabética até a primeira vez que for reordenado.
      </NotaSecao>
      {carga.tipo === 'carregando' && <TextoParam>Carregando…</TextoParam>}
      {carga.tipo === 'erro' && <TextoParam>{`Erro ao carregar: ${carga.mensagem}`}</TextoParam>}
      {carga.tipo === 'pronta' && original.length === 0 && (
        /*
          O ESTADO VAZIO DIZ O QUE ACONTECEU E O QUE FAZER, e não o que o desenvolvedor faria.
          O legado escrevia "Nenhum produto configurado ainda. Rode a migração de roteiro_produtos."
          (`admin.html:9482`), que manda a consultora rodar uma migração: ela não tem como, não sabe
          o que é, e a frase soa como sistema quebrado quando não há defeito nenhum. A tabela existe
          (`supabase/migrations/20260812_040_financeiro_agenda_parametros.sql:104`); o que falta é
          conteúdo, e agora o botão ao lado resolve isso na própria tela.
        */
        <div className="flex flex-col items-start gap-cx-3" data-testid="vazio-roteiro">
          <TextoParam>
            A sequência do roteiro ainda não foi criada. Crie a sequência inicial, com os 8 produtos em ordem alfabética, e
            depois arraste pra deixar na ordem que você usa no atendimento.
          </TextoParam>
          <Botao variante="primario" onClick={() => void semear()} disabled={semeando} data-testid="semear-roteiro">
            {semeando ? 'Criando...' : 'Criar sequência inicial'}
          </Botao>
        </div>
      )}
      {carga.tipo === 'pronta' && original.length > 0 && (
        <>
          <div ref={arraste.refDoGrupo} data-testid="lista-roteiro">
            <CartaoParam>
              {arraste.ordem.map((produto) => (
                <LinhaParam
                  key={produto}
                  data-produto={produto}
                  {...arraste.propsDaLinha(produto)}
                  // Cada linha marcada fica apagada (`.arrastando`, opacidade 0,4), uma a uma como no legado.
                  className={arraste.marcadas.has(produto) ? 'opacity-40' : ''}
                >
                  <AlcaDeArraste />
                  <span className="min-w-0 flex-1 text-cx-sm [overflow-wrap:anywhere]">
                    {`${numero.get(produto) ?? ''}. ${PRODUTOS_LEAD[produto] || produto}`}
                  </span>
                  {/* A nota do único produto recorrente (`admin.html:9493`). */}
                  {produto === 'acompanhamento_anual' && (
                    <span className="basis-full text-cx-xs text-texto-suave md:max-w-[340px] md:basis-auto md:text-right">
                      Único produto recorrente: envia lembrete de renovação por e-mail 45 dias antes do vencimento de cada
                      cliente
                    </span>
                  )}
                </LinhaParam>
              ))}
            </CartaoParam>
          </div>

          {/*
            UM BOTÃO PARA A TELA INTEIRA, no rodapé à direita, igual ao das outras sub-abas de
            Configuração. O texto ao lado diz se há ordem para gravar, porque aqui a alteração é
            difícil de ver: arrastar uma linha mexe na posição de várias, e os números continuam os
            do banco até o clique. Sem alteração ele fica desligado, em vez de aceitar um clique que
            gravaria a mesma ordem de novo.
          */}
          <div className="flex flex-wrap items-center justify-end gap-cx-3">
            <span className="text-cx-sm text-texto-suave" data-testid="contagem-roteiro-alterados">
              {ordemAlterada ? 'Ordem alterada, ainda não salva.' : 'Nenhuma alteração para salvar.'}
            </span>
            <Botao
              variante="primario"
              onClick={() => void salvarOrdem()}
              disabled={!ordemAlterada || salvando}
              data-testid="salvar-roteiro"
            >
              {salvando ? 'Salvando...' : 'Salvar alterações'}
            </Botao>
          </div>

          <LinhaDeStatus texto={status} testid="status-roteiro" />
        </>
      )}
      {/* O status da semeadura precisa aparecer com a lista vazia, onde o rodapé ainda não existe. */}
      {carga.tipo === 'pronta' && original.length === 0 && <LinhaDeStatus texto={status} testid="status-roteiro" />}
    </div>
  );
}
